import { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

export default function Footer() {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [errorMsg, setErrorMsg] = useState('');

  const handleSubmit = useCallback(async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    const form = e.currentTarget;
    const formData = new FormData(form);
    const honeypot = formData.get('website_alt') as string;
    if (honeypot && honeypot.trim() !== '') {
      setStatus('success');
      setEmail('');
      return;
    }

    const submitAddr = 'https://readdy.ai/api/form/d975hsv0d76aer3t5b50';

    try {
      const payload = new URLSearchParams();
      payload.append('email', email);
      const res = await fetch(submitAddr, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: payload.toString(),
      });
      const responseText = await res.text();
      let parsed: any = {};
      try { parsed = JSON.parse(responseText); } catch { /* ignore */ }

      if (res.ok && parsed?.code === 'OK') {
        setStatus('success');
        setEmail('');
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || '';
        setStatus('error');
        setErrorMsg(serverMsg || 'Wystąpił błąd. Spróbuj ponownie.');
      }
    } catch {
      setStatus('error');
      setErrorMsg('Wystąpił błąd sieci. Spróbuj ponownie.');
    }
  }, [email]);

  return (
    <footer className="bg-foreground-900 rounded-3xl mx-4 md:mx-6 mb-6 mt-4 overflow-hidden">
      <div className="max-w-7xl mx-auto px-6 md:px-10 py-14 md:py-20">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-10 md:gap-12">
          {/* Column 1: Brand */}
          <div className="sm:col-span-2 lg:col-span-1">
            <h3 className="font-heading text-2xl md:text-3xl font-bold text-background-50 tracking-tight">
              {t('footer.heading')}
            </h3>
          </div>

          {/* Column 2: Navigation */}
          <div>
            <h4 className="font-heading text-base font-semibold text-background-50 mb-4">
              {t('footer.nav_title')}
            </h4>
            <ul className="space-y-2.5">
              <li>
                <Link to="/artykul/kakobuy-spreadsheet-przewodnik-2026" className="text-sm text-foreground-300 hover:text-background-50 transition-colors cursor-pointer">
                  {t('footer.nav_spreadsheet')}
                </Link>
              </li>
              <li>
                <a href="https://kaspreadsheet.com" target="_blank" rel="noopener nofollow" className="text-sm text-foreground-300 hover:text-background-50 transition-colors cursor-pointer">
                  {t('footer.nav_finds')}
                </a>
              </li>
              <li>
                <Link to="/" className="text-sm text-foreground-300 hover:text-background-50 transition-colors cursor-pointer">
                  {t('footer.nav_comparisons')}
                </Link>
              </li>
              <li>
                <Link to="/artykul/kakobuy-spreadsheet-przewodnik-2026" className="text-sm text-foreground-300 hover:text-background-50 transition-colors cursor-pointer">
                  {t('footer.nav_guides')}
                </Link>
              </li>
              <li>
                <Link to="/" className="text-sm text-foreground-300 hover:text-background-50 transition-colors cursor-pointer">
                  {t('footer.nav_safety')}
                </Link>
              </li>
            </ul>
          </div>

          {/* Column 3: Social */}
          <div>
            <h4 className="font-heading text-base font-semibold text-background-50 mb-4">
              {t('footer.social_title')}
            </h4>
            <ul className="space-y-2.5">
              {[
                { icon: 'ri-instagram-line', label: 'Instagram' },
                { icon: 'ri-youtube-line', label: 'YouTube' },
                { icon: 'ri-facebook-line', label: 'Facebook' },
              ].map((social) => (
                <li key={social.label}>
                  <a
                    href="#"
                    target="_blank"
                    rel="noopener nofollow"
                    className="flex items-center gap-2.5 text-sm text-foreground-300 hover:text-background-50 transition-colors cursor-pointer"
                  >
                    <i className={`${social.icon} w-4 h-4 flex items-center justify-center`}></i>
                    <span>{social.label}</span>
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Column 4: Newsletter */}
          <div>
            <h4 className="font-heading text-base font-semibold text-background-50 mb-1">
              {t('footer.newsletter_title')}
            </h4>
            <p className="text-xs text-foreground-400 mb-4">
              {t('footer.newsletter_subtitle')}
            </p>
            <form onSubmit={handleSubmit} className="flex" data-readdy-form="">
              <input
                type="text"
                name="website_alt"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                readOnly
                className="visually-hidden"
              />
              <input
                type="email"
                name="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={t('footer.newsletter_placeholder')}
                required
                className="flex-1 bg-foreground-800 border border-foreground-700 rounded-l-full px-4 py-2.5 text-sm text-background-50 placeholder:text-foreground-500 focus:outline-none focus:border-primary-400 transition-colors"
              />
              <button
                type="submit"
                className="flex items-center justify-center w-11 h-auto rounded-r-full bg-background-50 text-foreground-900 hover:bg-background-100 transition-colors cursor-pointer"
              >
                <i className="ri-arrow-right-line text-sm"></i>
              </button>
            </form>
            {status === 'success' && (
              <p className="mt-2 text-xs text-accent-400">{t('footer.newsletter_success')}</p>
            )}
            {status === 'error' && (
              <p className="mt-2 text-xs text-red-400">{errorMsg || t('footer.newsletter_error')}</p>
            )}
          </div>
        </div>

        {/* Bottom decorative brand text */}
        <div className="mt-16 md:mt-20">
          <p className="font-heading text-[60px] md:text-[100px] font-extrabold text-foreground-800/20 leading-none select-none overflow-hidden whitespace-nowrap tracking-tighter">
            KAKOBUYFINDS
          </p>
        </div>

        {/* Copyright */}
        <div className="mt-6 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 text-xs text-foreground-500">
          <span>{t('footer.copyright')}</span>
          <span>
            <a href="https://kaspreadsheet.com" target="_blank" rel="noopener nofollow" className="hover:text-background-50 transition-colors cursor-pointer">
              kaspreadsheet.com
            </a>
          </span>
        </div>
      </div>
    </footer>
  );
}